import { activePage, replaceActivePage } from "../native/pages";
import type { EngineDocument, SceneNode } from "../scene/model";
import type { SelectionRegion } from "../native/region";
import {
  type AffineMatrix,
  invertAffine,
  transformPoint,
} from "../geometry/affine";
import { clippingBase } from "../native/groups";
import { regionRaster } from "./native-region-raster";
import type { ImageStore } from "./image-store";
import { createNativeNode } from "../native/document";
import { supportsReliableCanvasBlurFilters } from "./native-pixel-effects.js";
import { blurMask } from "./native-mask-blur";
export function traceRegion(ctx: CanvasRenderingContext2D, r: SelectionRegion) {
  ctx.beginPath();
  const b = r.box;
  if (r.kind === "circle")
    ctx.ellipse(
      b.x + b.width / 2,
      b.y + b.height / 2,
      b.width / 2,
      b.height / 2,
      0,
      0,
      Math.PI * 2,
    );
  else if (r.kind === "rectangle") ctx.rect(b.x, b.y, b.width, b.height);
  else {
    r.points.forEach((p, i) =>
      i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y),
    );
    ctx.closePath();
  }
}
export function trimRaster(
  canvas: HTMLCanvasElement,
  matrix: AffineMatrix,
  source: SceneNode,
  id = source.id,
) {
  const w = canvas.width,
    h = canvas.height,
    p = canvas.getContext("2d")!.getImageData(0, 0, w, h).data;
  let l = w,
    t = h,
    r = -1,
    b = -1;
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++)
      if (p[(y * w + x) * 4 + 3]) {
        l = Math.min(l, x);
        t = Math.min(t, y);
        r = Math.max(r, x);
        b = Math.max(b, y);
      }
  if (r < l) return null;
  const sx = Math.hypot(matrix[0], matrix[1]),
    sy = (matrix[0] * matrix[3] - matrix[1] * matrix[2]) / sx;
  // The scene minimum is one document pixel. Retain transparent padding for
  // smaller remnants instead of stretching their surviving source pixels.
  r = Math.min(w - 1, Math.max(r, l + Math.ceil(1 / sx - 1e-8) - 1));
  l = Math.max(0, Math.min(l, r - Math.ceil(1 / sx - 1e-8) + 1));
  b = Math.min(h - 1, Math.max(b, t + Math.ceil(1 / sy - 1e-8) - 1));
  t = Math.max(0, Math.min(t, b - Math.ceil(1 / sy - 1e-8) + 1));
  const trim = document.createElement("canvas");
  trim.width = r - l + 1;
  trim.height = b - t + 1;
  trim
    .getContext("2d")!
    .drawImage(
      canvas,
      l,
      t,
      trim.width,
      trim.height,
      0,
      0,
      trim.width,
      trim.height,
    );
  const asset = {
    id: crypto.randomUUID(),
    kind: "image" as const,
    source: trim.toDataURL("image/png"),
  };
  const center = transformPoint(
    { x: l + trim.width / 2, y: t + trim.height / 2 },
    matrix,
  );
  const node = createNativeNode(
    id,
    "image",
    center.x,
    center.y,
    Math.max(1, trim.width * sx),
    Math.max(1, trim.height * sy),
  );
  const rotation = (Math.atan2(matrix[1], matrix[0]) * 180) / Math.PI,
    skew =
      (Math.atan((matrix[0] * matrix[2] + matrix[1] * matrix[3]) / (sx * sy)) *
        180) /
      Math.PI;
  if (rotation) node.transform.rotation = rotation;
  if (skew) node.transform.skewX = skew;
  trim.width = trim.height = 0;
  node.layer = { ...source.layer, clipToBelow: false };
  node.appearance.visible = source.appearance.visible !== false;
  node.media.image = asset.id;
  return { node, asset };
}
export async function applyRegion(
  document: EngineDocument,
  targetId: string,
  region: SelectionRegion,
  action: "copy" | "cut" | "delete" | "clipboard",
  images: ImageStore,
  current: () => boolean,
) {
  const page = activePage(document),
    target = page.nodes.find((n) => n.id === targetId);
  if (!target || target.layer.locked || target.appearance.visible === false)
    throw new Error("Выберите один доступный слой.");
  const base = target.layer.clipToBelow
      ? clippingBase(page.nodes, target)
      : undefined,
    ids = new Set([
      target.media.image,
      target.media.interactiveImage,
      base?.media.image,
      base?.media.interactiveImage,
    ]);
  await images.prepare(document.assets.filter((a) => ids.has(a.id)));
  if (!current()) throw new Error("Обработка области отменена.");
  const raster = regionRaster(document, target, region.feather, images),
    { canvas, matrix } = raster,
    mask = globalThis.document.createElement("canvas"),
    part = globalThis.document.createElement("canvas");
  try {
    mask.width = part.width = canvas.width;
    mask.height = part.height = canvas.height;
    const m = mask.getContext("2d")!;
    m.setTransform(...invertAffine(matrix)!);
    m.fillStyle = "#ffffff";
    const blur = region.feather * raster.blurScale,
      nativeBlur = !blur || supportsReliableCanvasBlurFilters();
    if (blur && nativeBlur) m.filter = `blur(${blur}px)`;
    traceRegion(m, region);
    m.fill();
    if (blur && !nativeBlur)
      m.putImageData(
        blurMask(m.getImageData(0, 0, mask.width, mask.height), blur),
        0,
        0,
      );
    const pc = part.getContext("2d")!;
    pc.drawImage(canvas, 0, 0);
    pc.globalCompositeOperation = "destination-in";
    pc.drawImage(mask, 0, 0);
    const extracted = trimRaster(part, matrix, target, crypto.randomUUID());
    if (!extracted) throw new Error("В выбранной области нет пикселей.");
    extracted.node.layer.name =
      String(target.layer.name ?? "Слой") + " — фрагмент";
    const retainAppearance = (node: SceneNode) => {
      if (raster.exact) {
        node.appearance.opacity = target.appearance.opacity ?? 1;
        if (target.appearance.blendMode)
          node.appearance.blendMode = target.appearance.blendMode;
      }
    };
    retainAppearance(extracted.node);
    const additions = action === "delete" ? [] : [extracted.asset];
    let nodes = page.nodes.slice();
    if (action === "cut" || action === "delete") {
      const c = canvas.getContext("2d")!;
      c.setTransform(1, 0, 0, 1, 0, 0);
      c.globalCompositeOperation = "destination-out";
      c.drawImage(mask, 0, 0);
      const remainder = trimRaster(canvas, matrix, target);
      nodes = nodes.flatMap((n) =>
        n.id !== target.id ? [n] : remainder ? [remainder.node] : [],
      );
      if (remainder) {
        retainAppearance(remainder.node);
        additions.push(remainder.asset);
      }
    }
    if (action === "copy" || action === "cut") nodes.push(extracted.node);
    await images.prepare(additions);
    if (!current()) throw new Error("Обработка области отменена.");
    return {
      document: replaceActivePage(
        { ...document, assets: [...document.assets, ...additions] },
        { ...page, nodes },
      ),
      fragment: extracted,
    };
  } finally {
    raster.dispose();
    mask.width = mask.height = part.width = part.height = 0;
  }
}
